import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, Radio, RadioChangeEvent, Row, Select, Space, Upload } from "antd";

import { useAsyncAxios, utilAxiosFormWithAuth, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import type { UploadFile } from "antd/es/upload/interface";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";

import * as Types from "../commons/typeDTO";
import * as Request from "../commons/request";

const formItemLayout = {
    labelCol: {
        sm: {
            span: 5,
        },
    },
    wrapperCol: {
        sm: {
            span: 19,
        },
    },
};

function RegisterCatalog({ editMode, catalog, onRegisterCatalog }: { editMode: string; catalog?: Types.CatalogDto; onRegisterCatalog?: () => void }) {
    const [form] = Form.useForm();
    const alert = useAlert();

    const [catalogName, setCatalogName] = useState("");
    const [catalogVersion, setCatalogVersion] = useState("");
    const [attachFiles, setAttachFiles] = useState<Array<any>>([]);

    const requestAxiosAddCatalog = async (data: any) => {
        // console.log("data : " + data);

        const response = await utilAxiosFormWithAuth().post(Request.CATALOG_URL, data);

        return response.data;
    };

    const {
        loading: loadingAddCatalog,
        error: errorAddCatalog,
        data: resultAddCatalog,
        execute: requestAddCatalog,
    } = useAsyncAxios(requestAxiosAddCatalog);

    useEffect(() => {
        if (catalog === undefined) return;

        form.setFieldsValue(catalog);
    }, [catalog]);

    useEffect(() => {
        if (resultAddCatalog === null) return;

        if (onRegisterCatalog) onRegisterCatalog();

        alert.setAlert(AlertType.SUCCESS, "카탈로그 추가 성공", "카탈로그 추가를 완료하였습니다.");
    }, [resultAddCatalog]);

    useEffect(() => {
        if (errorAddCatalog === null) return;

        console.log("errorCatalog", errorAddCatalog);
        alert.setAlert(errorAddCatalog.response.data.code, "카탈로그 추가 실패", errorAddCatalog.response.data.message);
    }, [errorAddCatalog]);

    const requestAxiosUpdateCatalog = async (data: any, catalogId: number) => {
        const response = await utilAxiosFormWithAuth().put(Request.CATALOG_URL + "/" + catalogId, data);

        return response.data;
    };

    const {
        loading: loadingUpdateCatalog,
        error: errorUpdateCatalog,
        data: resultUpdateCatalog,
        execute: requestUpdateCatalog,
    } = useAsyncAxios(requestAxiosUpdateCatalog);

    useEffect(() => {
        if (resultUpdateCatalog === null) return;

        if (onRegisterCatalog) onRegisterCatalog();

        alert.setAlert(AlertType.SUCCESS, "카탈로그 수정 성공", "카탈로그 수정을 완료하였습니다.");
    }, [resultUpdateCatalog]);

    useEffect(() => {
        if (errorUpdateCatalog === null) return;

        console.log("errorUpdateCatalog", errorUpdateCatalog);
        alert.setAlert(errorUpdateCatalog.response.data.code, "카탈로그 수정 실패", errorUpdateCatalog.response.data.message);
    }, [errorUpdateCatalog]);

    const onFinish = async (values: any) => {
        const formData = new FormData();
        formData.append("catalogDto", new Blob([JSON.stringify(values)], { type: "application/json" }));

        for (const file in attachFiles) {
            const response = await fetch(URL.createObjectURL(attachFiles[file]));
            const blob = await response.blob();
            const data = new File([blob], attachFiles[file].name, { type: blob.type });

            formData.append("catalogFile", data);
        }
        if (editMode === "add") requestAddCatalog(formData);
        else if (editMode === "update") requestUpdateCatalog(formData, values.catalogId);
    };

    const propsAttachFiles = {
        onRemove: (file: any) => {
            const index = attachFiles.indexOf(file);
            const newAttachFiles = attachFiles.slice();
            newAttachFiles.splice(index, 1);
            setAttachFiles(newAttachFiles);
        },
        beforeUpload: (file: any) => {
            setAttachFiles([...attachFiles, file]);
            return false;
        },
        fileList: attachFiles,
    };

    return (
        <div>
            <Form form={form} onFinish={onFinish}>
                <Space size={20} direction="vertical" style={{ width: "100%" }}>
                    <Row>
                        <Col span={12}>
                            <Form.Item className="searchForm" {...formItemLayout} name="catalogId" label="카탈로그 ID">
                                <Input readOnly />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                className="searchForm"
                                {...formItemLayout}
                                name="catalogName"
                                label="카탈로그 이름"
                                rules={[
                                    {
                                        required: true,
                                        message: "카탈로그 이름을 입력해 주세요.",
                                    },
                                ]}
                            >
                                <Input placeholder="카탈로그 이름을 입력해 주세요." onChange={(e) => setCatalogName(e.target.value)} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                className="searchForm"
                                {...formItemLayout}
                                name="catalogVersion"
                                label="카탈로그 버젼"
                                rules={[
                                    {
                                        required: true,
                                        message: "카탈로그 버젼을 입력해 주세요.",
                                    },
                                ]}
                            >
                                <Input placeholder="카탈로그 버젼을 입력해 주세요." onChange={(e) => setCatalogVersion(e.target.value)} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                className="searchForm"
                                {...formItemLayout}
                                name="catalogFile"
                                label="카탈로그 파일"
                                rules={[
                                    {
                                        required: editMode === "add" ? true : false,
                                        message: "카탈로그 파일을 추가해 주세요.",
                                    },
                                ]}
                            >
                                <Upload {...propsAttachFiles} multiple={true}>
                                    <Button icon={<UploadOutlined />}>Upload PDF only</Button>
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify="end">
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                {editMode === "add" ? "등록" : "수정"}
                            </Button>
                        </Form.Item>
                    </Row>
                </Space>
            </Form>
        </div>
    );
}

export default RegisterCatalog;
